import type { SortDescriptor } from "react-stately";

import { memo, useState } from "react";

import type { RequestFilterType } from "../../../types/filters";
import type { CompletedRequest } from "../../../types/tables";

import { completedTableConfig } from "../../../config/tables/customer/completedTableConfig";
import { useCompletedRequests } from "../../../hooks";
import {
  useReopenRequest,
  useRequestRowActions,
  useResetFilterOnEmptySearch,
} from "../../../hooks/shared";
import { BulkDuplicateModal } from "../../modals/BulkDuplicateModal";
import { ConfigurableTable } from "../../tables";

interface CompletedTabProps {
  onFilterChange?: (filter: RequestFilterType) => void;
  searchValue?: string;
  selectedFilter?: RequestFilterType;
}

function CompletedTabComponent({
  onFilterChange,
  searchValue,
  selectedFilter,
}: CompletedTabProps) {
  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
    column: completedTableConfig.defaultSort?.column || "completedDate",
    direction:
      completedTableConfig.defaultSort?.direction === "desc"
        ? "descending"
        : "ascending",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  const { customProps: rowActionProps, duplicateModalProps } =
    useRequestRowActions();
  const reopenMutation = useReopenRequest();
  const customProps = {
    ...rowActionProps,
    onReopen: (requestId: number) => reopenMutation.mutate(requestId),
  };

  const {
    data: requestsData,
    isFetching,
    isLoading,
    isSuccess,
  } = useCompletedRequests({
    filter: selectedFilter,
    limit: itemsPerPage,
    page: currentPage,
    search: searchValue,
    sortDescriptor,
  });

  useResetFilterOnEmptySearch({
    // Never the broadest scope ("All" tab is), so always escalate.
    isAlreadyBroadest: false,
    isReady: isSuccess && !isFetching,
    onSwitchToAll: () => onFilterChange?.("all"),
    searchTerm: searchValue,
    totalMatches: requestsData?.total ?? 0,
  });

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  const completedRequests = requestsData?.requests || [];

  return (
    <div className="space-y-6">
      <ConfigurableTable
        config={completedTableConfig}
        customProps={customProps}
        data={completedRequests as CompletedRequest[]}
        isLoading={isLoading}
        paginationProps={{
          metadata: {
            currentPage,
            itemsPerPage,
            totalItems: requestsData?.total ?? null,
            totalPages: requestsData?.pagination?.totalPages ?? null,
          },
          onItemsPerPageChange: handleItemsPerPageChange,
          onPageChange: handlePageChange,
          pageSizeOptions: [10, 25, 50, 100],
          showPageSizeSelector: true,
        }}
        setSortDescriptor={setSortDescriptor}
        sortDescriptor={sortDescriptor}
        useAdvancedSorting={true}
      />

      <BulkDuplicateModal {...duplicateModalProps} />
    </div>
  );
}

export const CompletedTab = memo(CompletedTabComponent);
